<div class="dash-shell">
  <!-- ═══════════════════════════════════════════════════════════════════════
       PAGE HEADER
       ═══════════════════════════════════════════════════════════════════════ -->
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/dashboard">Admin</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >Recent emails</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Recent emails</h1>
        <p class="page-head__subtitle">
          Every platform notification sent across Kickstart Cash and Debtors Finance — its category,
          recipient and send status. Internal only; never shown to clients.
        </p>
      </div>
    </div>
  </header>

  <!-- ─── Send summary ──────────────────────────────────────────────────── -->
  @if (summary(); as s) {
    <section class="dash-section" aria-label="Email send summary">
      <div class="dash-kpi-grid dash-kpi-grid--3">
        <div class="dash-kpi dash-kpi--compact">
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="tag" [size]="18" /></span>
          </div>
          <div>
            <p class="dash-kpi__value">{{ s.total_emails | dash }}</p>
            <p class="dash-kpi__label">Emails</p>
          </div>
        </div>

        <div class="dash-kpi dash-kpi--compact">
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="check-circle" [size]="18" /></span>
          </div>
          <div>
            <p class="dash-kpi__value">{{ s.sent | dash }}</p>
            <p class="dash-kpi__label">Sent</p>
          </div>
        </div>

        <div class="dash-kpi dash-kpi--compact">
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="x-circle" [size]="18" /></span>
          </div>
          <div>
            <p class="dash-kpi__value">{{ s.failed | dash }}</p>
            <p class="dash-kpi__label">Failed</p>
          </div>
        </div>
      </div>
    </section>
  }

  <!-- ─── Filters ───────────────────────────────────────────────────────── -->
  <section class="aiev-filters" aria-label="Filters">
    <mat-form-field appearance="outline" class="aiev-filters__field">
      <mat-label>Category</mat-label>
      <mat-select [value]="categoryFilter()" (valueChange)="onCategoryChange($event)">
        <mat-option value="">All categories</mat-option>
        @for (c of categoryOptions(); track c.value) {
          <mat-option [value]="c.value">{{ c.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="aiev-filters__field">
      <mat-label>Status</mat-label>
      <mat-select [value]="statusFilter()" (valueChange)="onStatusChange($event)">
        <mat-option value="">All statuses</mat-option>
        @for (s of statusOptions(); track s.value) {
          <mat-option [value]="s.value">{{ s.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
  </section>

  <!-- ═══════════════════════════════════════════════════════════════════════
       TABLE
       ═══════════════════════════════════════════════════════════════════════ -->
  <section class="dash-section" aria-label="Email log">
    <div class="sg-panel dash-panel">
      <div class="dash-table-wrap">
        @if (loading()) {
          <table class="sg-table sg-table--skeleton">
            <thead>
              <tr>
                <th>Category</th>
                <th>Subject</th>
                <th>Recipient</th>
                <th>Status</th>
                <th class="sg-cell--num">Clicks</th>
                <th>Sent</th>
              </tr>
            </thead>
            <tbody>
              @for (r of [1, 2, 3, 4, 5, 6, 7, 8]; track r) {
                <tr>
                  <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w70"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
                  <td class="sg-cell--num">
                    <span
                      class="sg-skeleton sg-skeleton--text"
                      style="width: 50%; margin-left: auto"
                    ></span>
                  </td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span></td>
                </tr>
              }
            </tbody>
          </table>
        } @else if (error()) {
          <div class="sg-empty sg-empty--bordered">
            <app-icon name="x-circle" [size]="40" />
            <p class="sg-empty__title">Couldn't load emails</p>
            <p class="sg-empty__desc">Something went wrong on our side.</p>
            <button mat-stroked-button (click)="retry()">
              <app-icon name="refresh" [size]="18" /> Try again
            </button>
          </div>
        } @else if (!emails().length) {
          <div class="dash-chart-empty dash-chart-empty--pad">
            <app-icon name="tag" [size]="32" />
            <p>No emails match this filter yet</p>
          </div>
        } @else {
          <table class="sg-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Subject</th>
                <th>Recipient</th>
                <th>Status</th>
                <th class="sg-cell--num">Clicks</th>
                <th>Sent</th>
              </tr>
            </thead>
            <tbody>
              @for (e of emails(); track $index) {
                <tr class="sg-table__row">
                  <td>
                    <span class="dash-email-cat">{{ e.category_display }}</span>
                  </td>
                  <td class="email-subject">{{ e.subject }}</td>
                  <td class="aiev-muted">{{ e.to_email }}</td>
                  <td><app-status-chip [status]="e.status" /></td>
                  <td class="sg-cell--num aiev-muted">{{ e.click_count | number }}</td>
                  <td class="aiev-muted">{{ e.created_at | date: 'medium' }}</td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>

      <!-- ─── Pagination footer ─────────────────────────────────────────── -->
      @if (!loading() && !error() && showPager()) {
        <div class="aiev-pager">
          <span class="aiev-pager__range">
            {{ rangeStart() }}–{{ rangeEnd() }} of {{ total() | number }}
          </span>
          <div class="aiev-pager__nav">
            <button mat-stroked-button type="button" (click)="prevPage()" [disabled]="page() <= 1">
              <app-icon name="chevron-left" [size]="16" /> Previous
            </button>
            <button mat-stroked-button type="button" (click)="nextPage()" [disabled]="!hasNext()">
              Next <app-icon name="chevron-right" [size]="16" />
            </button>
          </div>
        </div>
      }
    </div>
  </section>
</div>
